import { Empty, Grid, Progress, Table } from "antd";
import {
  CommercialDimensionRow,
  CommercialTrendRow,
} from "../../shared/protocols/editor/PtlCommercialAnalyticsQuery";
import {
  KpiBox,
  KpiGrid,
  KpiHint,
  KpiLabel,
  KpiValue,
  RateCell,
  Section,
  SectionHeader,
  TrendBar,
  TrendBarFill,
  TrendBarSegment,
  TrendBucket,
  TrendHeader,
  TrendLegend,
  TrendLegendItem,
  TrendMetric,
  TrendMetricLabel,
  TrendMetrics,
  TrendRow,
} from "./commercialAnalyticsStyles";
import { formatInteger, formatPercent } from "./commercialAnalyticsUtils";

export interface KpiItem {
  label: string;
  value: string;
  hint?: string;
}

export function KpiStrip({ items }: { items: KpiItem[] }) {
  return (
    <KpiGrid>
      {items.map((item) => (
        <KpiBox key={item.label}>
          <KpiLabel>{item.label}</KpiLabel>
          <KpiValue>{item.value}</KpiValue>
          <KpiHint>{item.hint || " "}</KpiHint>
        </KpiBox>
      ))}
    </KpiGrid>
  );
}

export function TrendPanel({
  title,
  rows,
  loading,
  secondaryLabel,
  secondaryValue,
  barSeries,
}: {
  title: string;
  rows: CommercialTrendRow[];
  loading: boolean;
  secondaryLabel: string;
  secondaryValue: (row: CommercialTrendRow) => number;
  barSeries?: Array<{
    label: string;
    color: string;
    value: (row: CommercialTrendRow) => number;
  }>;
}) {
  const getBarTotal = (row: CommercialTrendRow) => barSeries
    ? barSeries.reduce((total, series) => total + series.value(row), 0)
    : row.count;
  const maximum = Math.max(1, ...rows.map(getBarTotal));
  return (
    <Section>
      <SectionHeader>{title}</SectionHeader>
      {loading ? (
        <Table loading pagination={false} dataSource={[]} columns={[]} size="small" />
      ) : rows.length ? (
        <div>
          {barSeries && (
            <TrendLegend>
              {barSeries.map((series) => (
                <TrendLegendItem key={series.label} $color={series.color}>{series.label}</TrendLegendItem>
              ))}
            </TrendLegend>
          )}
          <TrendHeader>
            <span>时间</span>
            <span>上报量</span>
            <TrendMetrics>
              <span>玩家</span>
              <span>成功</span>
              <span>{secondaryLabel}</span>
            </TrendMetrics>
          </TrendHeader>
          {rows.map((row) => (
            <TrendRow key={row.bucket}>
              <TrendBucket>{row.bucket}</TrendBucket>
              <TrendBar title={barSeries
                ? barSeries.map((series) => `${series.label} ${formatInteger(series.value(row))} 次`).join("；")
                : `${formatInteger(row.count)} 次`}
              >
                {barSeries
                  ? barSeries.map((series) => (
                    <TrendBarSegment
                      key={series.label}
                      $percent={(series.value(row) / maximum) * 100}
                      $color={series.color}
                    />
                  ))
                  : <TrendBarFill $percent={(row.count / maximum) * 100} />}
              </TrendBar>
              <TrendMetrics>
                <TrendMetric>
                  <TrendMetricLabel>玩家</TrendMetricLabel>
                  <span>{formatInteger(row.userCount)}</span>
                </TrendMetric>
                <TrendMetric>
                  <TrendMetricLabel>成功</TrendMetricLabel>
                  <span>{formatInteger(row.successCount)}</span>
                </TrendMetric>
                <TrendMetric>
                  <TrendMetricLabel>{secondaryLabel}</TrendMetricLabel>
                  <span>{formatInteger(secondaryValue(row))}</span>
                </TrendMetric>
              </TrendMetrics>
            </TrendRow>
          ))}
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前筛选范围暂无趋势数据" />
      )}
    </Section>
  );
}

export function DimensionTable({
  rows,
  loading,
  showCompletion,
}: {
  rows: CommercialDimensionRow[];
  loading: boolean;
  showCompletion?: boolean;
}) {
  const screens = Grid.useBreakpoint();
  const compact = screens.md === false;
  const tableWidth = showCompletion ? 332 : 264;

  return (
    <Table<CommercialDimensionRow>
      rowKey="key"
      size="small"
      loading={loading}
      dataSource={rows}
      pagination={false}
      scroll={{ x: compact ? tableWidth : undefined, y: 260 }}
      columns={[
        { title: "分类", dataIndex: "key", width: compact ? 88 : undefined, ellipsis: true },
        { title: "上报", dataIndex: "count", width: compact ? 56 : 72, render: formatInteger },
        { title: "玩家", dataIndex: "userCount", width: compact ? 52 : 72, render: formatInteger },
        { title: "成功率", dataIndex: "successRate", width: compact ? 68 : 124, render: renderRateCell },
        ...(showCompletion
          ? [{ title: "完播率", dataIndex: "completionRate", width: compact ? 68 : 124, render: renderRateCell }]
          : []),
      ]}
    />
  );
}

function renderRateCell(value: unknown) {
  const ratio = Math.max(0, Math.min(1, Number(value) || 0));
  const color = ratio >= 0.8 ? "#16a34a" : ratio >= 0.5 ? "#d97706" : "#dc2626";
  return (
    <RateCell>
      <Progress percent={ratio * 100} size="small" showInfo={false} strokeColor={color} />
      <span>{formatPercent(ratio)}</span>
    </RateCell>
  );
}
